/**
 * Game Record Service
 *
 * Queries the existing `games` collection for completed multiplayer games.
 * No separate collection needed - all game data (moveHistory, players, result)
 * is already stored in the game documents from live play.
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebaseConfig";

export interface PlayerInfo {
  uid: string;
  username: string;
}

/**
 * Get all registered players (uid + username).
 * Used by dev tools to switch between player histories.
 */
export async function getAllPlayers(): Promise<PlayerInfo[]> {
  const usersCollection = collection(db, "users");
  const snapshot = await getDocs(usersCollection);
  const players: PlayerInfo[] = [];

  snapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    const username = data.profile?.username;
    if (username) {
      players.push({ uid: docSnapshot.id, username });
    }
  });

  players.sort((a, b) => a.username.localeCompare(b.username));
  return players;
}

// Default collection matches the env var used by firebase.ts for live game writes
const DefaultCollectionName =
  process.env.REACT_APP_FIREBASE_GAMES_COLLECTION || "games";

export interface GameRecordDocument {
  id: string;
  lightPlayerId: string;
  darkPlayerId: string;
  lightPlayerUsername: string;
  darkPlayerUsername: string;
  lightKing: string;
  darkKing: string;
  fenHistory: string[];
  result: 'light' | 'dark' | 'draw';
  winCondition: string;
  turnCount: number;
  timestamp: number;
  timeControlPreset: string;
  gameName?: string;
  isBotGame?: boolean;
  evalHistory?: number[];  // Per-move board eval from Light's perspective (Arena games)
}

/**
 * Convert a raw Firebase game document into a GameRecordDocument.
 * Maps the existing game fields to replay-friendly names.
 */
function mapGameDoc(id: string, data: any): GameRecordDocument | null {
  // Skip games that aren't finished
  // Also recognise state === 'Draw' for bot games where lightWon may not have been set
  const isDraw = data.lightWon === 'draw' || data.state === 'Draw';
  if (!isDraw && (data.lightWon === '' || data.lightWon === undefined || data.lightWon === null)) {
    return null;
  }

  // Skip games with no meaningful move history
  if (!data.moveHistory || data.moveHistory.length <= 1) {
    return null;
  }

  // Determine result
  let result: 'light' | 'dark' | 'draw';
  if (isDraw) {
    result = 'draw';
  } else if (data.lightWon === true) {
    result = 'light';
  } else {
    result = 'dark';
  }

  return {
    id,
    lightPlayerId: data.lightPlayer || '',
    darkPlayerId: data.darkPlayer || '',
    lightPlayerUsername: data.lightPlayerUsername || 'Light',
    darkPlayerUsername: data.darkPlayerUsername || 'Dark',
    lightKing: data.lightKing || '',
    darkKing: data.darkKing || '',
    fenHistory: data.moveHistory || [],
    result,
    winCondition: data.winCondition || '',
    turnCount: data.turnCount || 0,
    timestamp: data.timestamp || 0,
    timeControlPreset: data.timeControlPreset || 'standard',
    gameName: data.gameName,
    isBotGame: data.isBotGame || false,
  };
}

/**
 * Get all completed game records for a user, ordered by timestamp descending.
 * Queries both lightPlayer and darkPlayer fields, merges and deduplicates.
 */
export async function getGameRecordsForUser(
  userId: string,
  limitCount: number = 50,
  collectionName?: string
): Promise<GameRecordDocument[]> {
  const col = collection(db, collectionName || DefaultCollectionName);
  const records = new Map<string, GameRecordDocument>();

  // Query where user is light player
  // Uses only equality filter (no orderBy) to avoid requiring composite Firestore indexes.
  // Sorting and limiting are done in JavaScript below.
  const lightQuery = query(
    col,
    where("lightPlayer", "==", userId)
  );

  const lightSnapshot = await getDocs(lightQuery);
  lightSnapshot.forEach((docSnapshot) => {
    const mapped = mapGameDoc(docSnapshot.id, docSnapshot.data());
    if (mapped) {
      records.set(docSnapshot.id, mapped);
    }
  });

  // Query where user is dark player
  const darkQuery = query(
    col,
    where("darkPlayer", "==", userId)
  );

  const darkSnapshot = await getDocs(darkQuery);
  darkSnapshot.forEach((docSnapshot) => {
    if (!records.has(docSnapshot.id)) {
      const mapped = mapGameDoc(docSnapshot.id, docSnapshot.data());
      if (mapped) {
        records.set(docSnapshot.id, mapped);
      }
    }
  });

  // Sort by timestamp descending and limit results
  return Array.from(records.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limitCount);
}

/**
 * Update the user-given name for a game.
 * Writes a `gameName` field to the existing game document.
 */
export async function updateGameName(
  gameId: string,
  gameName: string,
  collectionName?: string
): Promise<void> {
  const docRef = doc(db, collectionName || DefaultCollectionName, gameId);
  await updateDoc(docRef, { gameName });
}
